import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Generic per-user UI preference, backed by the RPCs added in
 * supabase/migrations/20260816090000_add_user_ui_preferences.sql
 * (get_user_ui_preference / set_user_ui_preference over public.users.ui_preferences).
 *
 * Any screen that needs a preference tied to the PERSON — not the browser — can use this
 * directly instead of reaching for localStorage. First consumer: useConversationsVersion.
 *
 * Untyped RPC call ((supabase as any).rpc): these two functions aren't in the generated
 * Database type yet because the migration hasn't been applied to the project the types were
 * generated from. Same shape already used elsewhere in this codebase for the identical reason
 * (see src/hooks/useConversationResetMarkers.ts).
 *
 * Write is optimistic and best-effort: onMutate updates the cache immediately so the UI never
 * waits on a round trip, and a failure rolls the cache back but does NOT throw past the caller —
 * screens that also keep their own instant, offline-safe copy (like localStorage) should just
 * keep that copy regardless of what this hook's write does. See useConversationsVersion for how
 * "keep working without the database" is layered on top of this.
 */

const QUERY_KEY_PREFIX = 'user-ui-preference';

export function userUiPreferenceQueryKey(key: string) {
  return [QUERY_KEY_PREFIX, key] as const;
}

interface UseUserUiPreferenceOptions<T> {
  /** Seeds the query so the first render already has a value instead of `undefined` while the
   *  network round trip is in flight — e.g. a synchronous localStorage read. Optional. */
  initialData?: () => T;
  /** Validates a value read back from the server before trusting it (guards against a stale or
   *  hand-edited row holding something the current frontend no longer understands). Values that
   *  fail validation are treated as "not set" (defaultValue). */
  isValid?: (value: unknown) => value is T;
  enabled?: boolean;
}

export function useUserUiPreference<T>(
  key: string,
  defaultValue: T,
  options: UseUserUiPreferenceOptions<T> = {},
) {
  const { initialData, isValid, enabled = true } = options;
  const queryClient = useQueryClient();
  const queryKey = userUiPreferenceQueryKey(key);

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }): Promise<T> => {
      const { data, error } = await (supabase as any)
        .rpc('get_user_ui_preference', { key })
        .abortSignal(signal);
      if (error) throw error;
      if (data === null || data === undefined) return defaultValue;
      if (isValid && !isValid(data)) return defaultValue;
      return data as T;
    },
    initialData,
    enabled,
    // With staleTime at its default (0), `initialData` renders instantly (no loading flicker)
    // and is treated as stale right away, so the first mount still fetches the real value in the
    // background and reconciles it in. What we deliberately turn OFF is refetching later: a UI
    // preference should not jump under the operator mid-session because a background refetch
    // (tab regaining focus, network reconnecting) picked up a value written from another device.
    // It resolves once per mount and then holds until this hook's own setValue changes it —
    // cross-device changes are picked up the next time the screen is opened fresh.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const mutation = useMutation({
    mutationFn: async (value: T) => {
      const { data, error } = await (supabase as any).rpc('set_user_ui_preference', {
        key,
        value: value as unknown,
      });
      if (error) throw error;
      return (data ?? value) as T;
    },
    onMutate: async (value: T) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<T>(queryKey);
      queryClient.setQueryData<T>(queryKey, value);
      return { previous };
    },
    onError: (_error, _value, context) => {
      if (context && 'previous' in context && context.previous !== undefined) {
        queryClient.setQueryData<T>(queryKey, context.previous);
      }
    },
    onSuccess: (confirmed) => {
      queryClient.setQueryData<T>(queryKey, confirmed);
    },
  });

  const setValue = useCallback(
    (value: T) => {
      mutation.mutate(value);
    },
    [mutation],
  );

  return {
    value: (query.data ?? defaultValue) as T,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    setValue,
  };
}
